import { lazy } from '@attain-apps/function-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import { Expression } from '../../expression'
import type * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { standardNodeCreator } from './utils'

/**
 * Creates a list with custom elements
 *
 * @param definition A list where each element is an expression
 * @example
 * {
 *   '[]': [
 *     { '@a': 'name' },
 *     { '@': 0 },
 *     {
 *       '{}': { d: { '@a': 'code' } }
 *     },
 *     {
 *       '[]': [ { '@': 2 } ]
 *     }
 *   ]
 * }
 * // ->
 * [
 *   'Room 100',
 *   'Room 100',
 *   { d: 'RM100' },
 *   [ { d: 'RM100' } ]
 * ]
 */
export interface CreateTuple<out T = readonly any[]> {
  /** {@link CreateTuple} */
  readonly [Key.CreateTuple]: T extends readonly unknown[]
    ? // NOTE: using T in CreateTuple.Elements would be ideal, but it causes a lot
      // of downstream issues
      CreateTuple.Elements<readonly any[]>
    : never
}
export namespace CreateTuple {
  export const isCreateTuple: TypeChecker<CreateTuple> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<CreateTuple>({
        [Key.CreateTuple]: Elements.isElements,
      }),
    ).usingAlias('Fn.CreateTuple')
  export const assertIsCreateTuple: TypeAsserter<CreateTuple> =
    TypeAsserter.Factory.from(isCreateTuple)

  export type Elements<out T extends readonly unknown[] = readonly any[]> = {
    [K in keyof T]: Expression<T[K]>
  }
  export namespace Elements {
    export const isElements: TypeChecker<Elements> = TypeChecker.Factory.lazy(
      () => TypeChecker.Factory.list(Expression.isExpression),
    )
  }

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends readonly unknown[] = readonly unknown[],
  > extends fnNode.Node<
    T,
    Key.CreateTuple,
    { [K in keyof T]: expressionNode.Node<T[K]> }
  > {
    public readonly type = common.value.types.Intrinsic.List

    constructor(arg: Node<T>['arg']) {
      super(Key.CreateTuple, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<CreateTuple> = {
    createNode: lazy(() =>
      standardNodeCreator.withProperties({
        fnKey: Key.CreateTuple,
        isFn: isCreateTuple,
        nodeClass: Node,
      }),
    ),
    expressionsInArgs: fn => Object.values(fn[Key.CreateTuple]),
    typeChecker: isCreateTuple,
  }
}
